import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useBlackouts } from './tabs/blackouts/useBlackouts';
import { useEgsDrafts } from './tabs/egs/useEgsDrafts';
import { useInclusionDrafts } from './tabs/inclusions/useInclusionDrafts';
import { usePackageOptionsState } from './tabs/packageOptions/usePackageOptionsState';
import { useOverrides } from './tabs/overrides/useOverrides';
import { usePropertySetup } from './tabs/propertySetup/usePropertySetup';
import { useScrapeSession } from './tabs/scrape/useScrapeSession';
import { useSurchargeGrids } from './tabs/surcharges/useSurchargeGrids';

// Mounted once at PricingToolPage so state here survives tab switches - including
// useScrapeSession's poll timer, whose cleanup now fires only when this unmounts, not per-tab.
interface PricingToolSessionValue {
  egs: ReturnType<typeof useEgsDrafts>;
  inclusions: ReturnType<typeof useInclusionDrafts>;
  propertySetup: ReturnType<typeof usePropertySetup>;
  blackouts: ReturnType<typeof useBlackouts>;
  overrides: ReturnType<typeof useOverrides>;
  scrape: ReturnType<typeof useScrapeSession>;
  packageOptions: ReturnType<typeof usePackageOptionsState>;
  optionNames: Record<number, string | undefined>;
  setOptionName: (optionIndex: number, name: string | undefined) => void;
  surcharges: ReturnType<typeof useSurchargeGrids>;
}

const PricingToolSessionContext = createContext<PricingToolSessionValue | undefined>(undefined);

export function PricingToolSessionProvider({ offerId, children }: { offerId?: string; children: React.ReactNode }) {
  const inclusions = useInclusionDrafts(offerId);
  const egs = useEgsDrafts(offerId);
  const propertySetup = usePropertySetup(offerId);
  const blackouts = useBlackouts(offerId);
  const overrides = useOverrides(offerId);
  const scrape = useScrapeSession(offerId);
  const packageOptions = usePackageOptionsState(offerId);
  const surcharges = useSurchargeGrids(offerId, scrape.stayInputs?.currencyCode);

  const [optionNames, setOptionNames] = useState<Record<number, string | undefined>>({});
  useEffect(() => setOptionNames({}), [offerId]);
  const setOptionName = useCallback((optionIndex: number, name: string | undefined) => {
    setOptionNames((prev) => ({ ...prev, [optionIndex]: name }));
  }, []);

  const value = useMemo(
    () => ({
      egs,
      inclusions,
      propertySetup,
      blackouts,
      overrides,
      scrape,
      packageOptions,
      optionNames,
      setOptionName,
      surcharges,
    }),
    [egs, inclusions, propertySetup, blackouts, overrides, scrape, packageOptions, optionNames, setOptionName, surcharges],
  );

  return <PricingToolSessionContext.Provider value={value}>{children}</PricingToolSessionContext.Provider>;
}

export function usePricingToolSession() {
  const context = useContext(PricingToolSessionContext);

  if (!context) {
    throw new Error('usePricingToolSession must be used within a PricingToolSessionProvider.');
  }

  return context;
}

export function useInclusionDraftsContext() {
  return usePricingToolSession().inclusions;
}

export function useEgsDraftsContext() {
  return usePricingToolSession().egs;
}

export function usePropertySetupContext() {
  return usePricingToolSession().propertySetup;
}

export function useBlackoutsContext() {
  return usePricingToolSession().blackouts;
}

export function useOverridesContext() {
  return usePricingToolSession().overrides;
}

export function useScrapeSessionContext() {
  return usePricingToolSession().scrape;
}

export function usePackageOptionsContext() {
  return usePricingToolSession().packageOptions;
}

export function usePricingOptionNamesContext() {
  const { optionNames, setOptionName } = usePricingToolSession();
  return { optionNames, setOptionName };
}

export function useSurchargeGridsContext() {
  return usePricingToolSession().surcharges;
}
